import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';


const JournalContext = createContext();

export const useJournalData = () => useContext(JournalContext);



export const JournalProvider = ({ children }) => {
    const [rows, setRows] = useState([]);

    const [isLoading, setIsLoading] = useState(false);

    const [accounts, setAccounts] = useState([]);

    const fetchAccounts = async () => {
        try {
            const response = await axios.get(`${API_URL}/accounts`);
            if (response.data) {
                setAccounts(response.data);
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching accounts:", error);
            return [];
        }
    };

    const [userProfile, setUserProfile] = useState(() => {
        const savedProfile = localStorage.getItem('userProfile');
        if (savedProfile) {
            try {
                return JSON.parse(savedProfile);
            } catch (e) {
                console.error("Failed to parse user profile", e);
            }
        }
        // Default temporary state until accounts are loaded
        return {
            id: null,
            name: "Loading...",
            currency: "USD ($)",
            status: "Active"
        };
    });

    // Initial Data Load
    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                // 1. Fetch Accounts
                const fetchedAccounts = await fetchAccounts();

                // 2. Initialize active profile if needed
                let currentProfileId = userProfile.id;
                let activeAccount = null;

                if (fetchedAccounts && fetchedAccounts.length > 0) {
                    // If no profile selected, or selected ID not in list (deleted?), pick first
                    const match = fetchedAccounts.find(a => a.id === currentProfileId);
                    if (match) {
                        activeAccount = match;
                    } else {
                        activeAccount = fetchedAccounts[0];
                    }

                    // Update profile state if needed OR if data refresh is needed (always refresh active account data)
                    if (activeAccount) {
                        const newProfile = {
                            ...activeAccount,
                            currency: activeAccount.currency === 'USD' ? 'USD ($)' : 'EUR (€)'
                        };

                        // Check if we need to update state (avoid loop if deep equal, but simple JSON check is fine)
                        // Actually, just always update to ensure fresh fields (broker, instrument_type) are present
                        if (JSON.stringify(newProfile) !== JSON.stringify(userProfile)) {
                            setUserProfile(newProfile);
                            localStorage.setItem('userProfile', JSON.stringify(newProfile));
                            currentProfileId = activeAccount.id;
                        }
                    }
                }

                // 3. Fetch Trades for the active account
                if (currentProfileId) {
                    const tradesResponse = await axios.get(`${API_URL}/trades?account_id=${currentProfileId}`);
                    if (tradesResponse.data) {
                        setRows(tradesResponse.data);
                    }
                }
            } catch (error) {
                console.error("Error initializing data:", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run once on mount

    const updateRow = async (index, updatedRow) => {
        const rowToUpdate = rows[index];
        if (!rowToUpdate.id) {
            console.error("Cannot update row without ID");
            return;
        }

        try {
            // Optimistic update
            const newRows = [...rows];
            newRows[index] = updatedRow;
            setRows(newRows);

            await axios.put(`${API_URL}/trades/${rowToUpdate.id}`, {
                date: updatedRow.date,
                asset: updatedRow.asset,
                side: updatedRow.side,
                catalyst_type: updatedRow.catalystType,
                catalyst_name: updatedRow.catalystName,
                thesis: updatedRow.thesis,
                scenario: updatedRow.scenario,
                view: updatedRow.view !== undefined ? updatedRow.view : rowToUpdate.view,
                entry: updatedRow.entry,
                sl: updatedRow.sl,
                tp: updatedRow.tp,
                size: updatedRow.size,
                exit: updatedRow.exit,
                pnl: updatedRow.pnl,
                pnl_percent: updatedRow.pnlPercent,
                exit_reason: updatedRow.exitReason,
                style: updatedRow.style,
                confidence_score: updatedRow.confidenceScore !== undefined ? updatedRow.confidenceScore : rowToUpdate.confidenceScore,
                setup_score: updatedRow.setupScore !== undefined ? updatedRow.setupScore : (updatedRow.setup_score !== undefined ? updatedRow.setup_score : rowToUpdate.setupScore),
                management_score: updatedRow.managementScore !== undefined ? updatedRow.managementScore : (updatedRow.management_score !== undefined ? updatedRow.management_score : rowToUpdate.managementScore),
                status: updatedRow.status
            });

            // Backup synchronization:
            if (updatedRow.status === 'Done') {
                axios.post(`${API_URL}/journal/add-trade`, {
                    ...updatedRow,
                    id: rowToUpdate.id
                }).catch(backupErr => {
                    console.warn("Could not backup done trade to JSON:", backupErr);
                });
            } else if (rowToUpdate.status === 'Done' || updatedRow.status === 'Active' || updatedRow.status === 'Ready') {
                // If trade is moved back to Active or Ready, ensure it is removed from the backup JSON
                axios.post(`${API_URL}/journal/remove-trade`, {
                    ...rowToUpdate,
                    ...updatedRow
                }).catch(err => {
                    console.warn("Could not remove trade from JSON backup:", err);
                });
            }
        } catch (error) {
            console.error("Error updating trade:", error);
            window.location.reload();
        }
    };

    const deleteRow = async (index) => {
        const rowToDelete = rows[index];
        if (!rowToDelete.id) {
            console.error("Cannot delete row without ID");
            return;
        }

        try {
            setRows(prev => prev.filter((_, i) => i !== index));
            await axios.delete(`${API_URL}/trades/${rowToDelete.id}`);
            // Also explicitly ensure it is purged from JSON backup if it was Done
            axios.post(`${API_URL}/journal/remove-trade`, rowToDelete).catch(err => {
                console.warn("Could not remove deleted trade from JSON backup:", err);
            });
        } catch (error) {
            console.error("Error deleting trade:", error);
            window.location.reload();
        }
    };

    const addRow = async () => {
        const newRowData = {
            date: new Date().toISOString().split('T')[0],
            asset: "NEW",
            side: "LONG",
            catalyst_type: "",
            catalyst_name: "",
            thesis: "",
            scenario: "",
            // context: "", // Removed
            // trigger: "", // Removed
            view: "", // Added view
            entry: "",
            sl: "",
            tp: "",
            size: "",
            exit: "",
            pnl: "",
            pnl_percent: "",
            exit_reason: "",
            style: "MACRO",
            confidence_score: null
        };

        try {
            const response = await axios.post(`${API_URL}/trades`, newRowData);
            if (response.data && response.data.id) {
                const newRow = {
                    id: response.data.id,
                    date: newRowData.date,
                    asset: newRowData.asset,
                    side: newRowData.side,
                    catalystType: newRowData.catalyst_type,
                    catalystName: newRowData.catalyst_name,
                    thesis: newRowData.thesis,
                    scenario: newRowData.scenario,
                    // context: newRowData.context,
                    // trigger: newRowData.trigger,
                    view: newRowData.view,
                    entry: newRowData.entry,
                    sl: newRowData.sl,
                    tp: newRowData.tp,
                    size: newRowData.size,
                    exit: newRowData.exit,
                    pnl: newRowData.pnl,
                    pnlPercent: newRowData.pnl_percent,
                    exitReason: newRowData.exit_reason,
                    style: newRowData.style,
                    confidenceScore: newRowData.confidence_score,
                    setupScore: newRowData.setup_score,
                    managementScore: newRowData.management_score,
                    setup_score: newRowData.setup_score,
                    management_score: newRowData.management_score
                };
                setRows(prev => [...prev, newRow]);
            }
        } catch (error) {
            console.error("Error creating trade:", error);
        }
    };

    const resetData = async () => {
        setIsLoading(true);
        try {
            // Artificial delay for UX feedback
            await new Promise(resolve => setTimeout(resolve, 400));
            const response = await axios.get(`${API_URL}/trades`);
            if (response.data) {
                setRows(response.data);
            }
        } catch (error) {
            console.error("Error reloading trades data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const reorderRows = (startIndex, endIndex) => {
        setRows(prev => {
            const result = Array.from(prev);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result;
        });
    };

    const setAllData = (newRows) => {
        setRows(newRows);
    };



    // ... (keep existing code)

    // Old userProfile state and updateUserProfile removed to avoid duplicates.
    // The new logic is handled above in the fetchAccounts flow.

    const switchAccount = async (account) => {
        if (!account) return;

        const newProfile = {
            ...account,
            currency: account.currency === 'USD' ? 'USD ($)' : 'EUR (€)'
        };

        setUserProfile(newProfile);
        localStorage.setItem('userProfile', JSON.stringify(newProfile));

        setIsLoading(true);
        try {
            const tradesResponse = await axios.get(`${API_URL}/trades?account_id=${account.id}`);
            if (tradesResponse.data) {
                setRows(tradesResponse.data);
            } else {
                setRows([]);
            }
        } catch (error) {
            console.error("Error switching account:", error);
            setRows([]);
        } finally {
            setIsLoading(false);
        }
    };

    const updateUserProfile = async (updatedProfile) => {
        try {
            if (updatedProfile.id) {
                await axios.put(`${API_URL}/accounts/${updatedProfile.id}`, updatedProfile);
            }
            setUserProfile(updatedProfile);
            localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
        } catch (error) {
            console.error("Error saving user profile to backend:", error);
            alert("Failed to save account settings. Check console.");
        }
    };


    const [dateFilter, setDateFilter] = useState('All');

    const filteredRows = React.useMemo(() => {
        if (dateFilter === 'All') return rows;

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        return rows.filter(row => {
            if (!row.date) return false;
            // Parse YYYY-MM-DD as local date
            const [y, m, d] = row.date.split('-').map(Number);
            const rowDateOnly = new Date(y, m - 1, d);

            switch (dateFilter) {
                case 'Today':
                    return rowDateOnly.getTime() === today.getTime();
                case 'Last 7 days':
                    const last7 = new Date(today);
                    last7.setDate(today.getDate() - 7);
                    return rowDateOnly >= last7;
                case 'Last 30 days':
                    const last30 = new Date(today);
                    last30.setDate(today.getDate() - 30);
                    return rowDateOnly >= last30;
                case 'Last 3 months':
                    const last3Months = new Date(today);
                    last3Months.setMonth(today.getMonth() - 3);
                    return rowDateOnly >= last3Months;
                case 'Last 6 months':
                    const last6Months = new Date(today);
                    last6Months.setMonth(today.getMonth() - 6);
                    return rowDateOnly >= last6Months;
                case 'Last year':
                    const lastYear = new Date(today);
                    lastYear.setFullYear(today.getFullYear() - 1);
                    return rowDateOnly >= lastYear;
                default:
                    return true;
            }
        });
    }, [rows, dateFilter]);

    const getRelativeDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return "Today";
        if (diffDays === 1) return "Yesterday";
        if (diffDays < 7) return `${diffDays} days ago`;
        return date.toLocaleDateString();
    };

    const filteredInitialBalance = React.useMemo(() => {
        const baseBalance = userProfile?.initial_balance || userProfile?.initialBalance || 0;
        if (!rows || rows.length === 0 || dateFilter === 'All') return baseBalance;

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        let filterDate = new Date(0);
        switch (dateFilter) {
            case 'Today':
                filterDate = today;
                break;
            case 'Last 7 days':
                filterDate = new Date(today);
                filterDate.setDate(today.getDate() - 7);
                break;
            case 'Last 30 days':
                filterDate = new Date(today);
                filterDate.setDate(today.getDate() - 30);
                break;
            case 'Last 3 months':
                filterDate = new Date(today);
                filterDate.setMonth(today.getMonth() - 3);
                break;
            case 'Last 6 months':
                filterDate = new Date(today);
                filterDate.setMonth(today.getMonth() - 6);
                break;
            case 'Last year':
                filterDate = new Date(today);
                filterDate.setFullYear(today.getFullYear() - 1);
                break;
            default:
                return baseBalance;
        }

        let priorPnl = 0;
        rows.forEach(row => {
            if (!row.date || row.status !== 'Done') return;
            const [y, m, d] = row.date.split('-').map(Number);
            const rowDateOnly = new Date(y, m - 1, d);

            if (rowDateOnly < filterDate) {
                const pnlString = row.pnl ? String(row.pnl).replace(/[^\d.-]/g, '') : '0';
                const pnl = parseFloat(pnlString);
                if (!isNaN(pnl)) {
                    priorPnl += pnl;
                }
            }
        });

        return baseBalance + priorPnl;
    }, [rows, dateFilter, userProfile]);

    return (
        <JournalContext.Provider value={{
            accounts,
            fetchAccounts,
            switchAccount,
            rows,
            filteredRows,
            dateFilter,
            setDateFilter,
            updateRow,
            deleteRow,
            addRow,
            resetData,
            reorderRows,
            setAllData,
            userProfile,
            updateUserProfile,
            getRelativeDate,
            isLoading,
            filteredInitialBalance
        }}>
            {children}
        </JournalContext.Provider>
    );
};
